import { DeleteOutlined, EyeOutlined, NotificationOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Col, DatePicker, Input, Row, Select, Space, Spin, Table, Tag, Tooltip } from 'antd';
import { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import queryString from 'query-string';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthProvider';
import { updateProps } from '../../../contextRedux/RequestFile/RequestFileModel';
import RequestFileService from '../../../services/requestFile/RequestFileService';
import { RootState } from '../../../store';
import { usePermission } from '../../../utils/usePermission';

const { Search } = Input;

interface DispatchProps {}

const Dispatch: FC<DispatchProps> = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const reduxRequestFile = useSelector((state: RootState) => state.models?.requestFile);
  const searchStatus = useSelector((state: RootState) => state.models.requestFile.props.searchStatus);
  const searchTerm = useSelector((state: RootState) => state.models.requestFile.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.requestFile.props.skip);
  const take = useSelector((state: RootState) => state.models?.requestFile.props.take);

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);

  const currentPage = Math.floor(reduxRequestFile.props.skip / reduxRequestFile.props.take) + 1;
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const pageSizes = [10, 20, 30, 50];
  const { message } = App.useApp();

  //const [isLoading, setIsLoading] = useState(false); // Loading state for getTime
  const { profile } = useAuth();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: data,
    isFetching: isRequestFileLoading,
    refetch: search,
  } = useQuery({
    queryKey: ['requestFiles', searchTerm, skip, take, searchStatus, selectedDate],
    queryFn: () =>
      RequestFileService.search({
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isStatusIncluded: true,
        isRequestFileSystemTypesIncluded: true,
        isAssignedTechnicianIncluded: true,
        termen: searchTerm,
        statusName: searchStatus,
        skip: skip,
        take: take,
        interventionDate: selectedDate?.format('YYYY-MM-DD') || undefined,
        //isActive: true,
      }).then((data) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 })); // Update total items
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      search();
    }
  }, [searchTerm, isSearchTermInitialized, search]);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }, []);

  const handleStatusChange = useCallback(
    (value: string) => {
      debouncedSearch(value, 'searchStatus');
    },
    [searchStatus]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value, 'searchTerm');
    },
    [searchTerm]
  );

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const debouncedSearch = useCallback(
    debounce((value: string, search: string) => {
      dispatch(updateProps({ field: search, value: value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1000),
    []
  );

  const { mutate: repostMutate, isPending: isRepostMutating } = useMutation({
    mutationFn: (record: any) => RequestFileService.sendNotification(record?.id, record),
    onSuccess: (data: any) => {
      if (data?.message) {
        message.success(data?.message);
      } else {
        message.error('Repost failed');
      }
    },
    onError: (error: any) => {
      console.error('Error processing your request:', error);
      message.error('There was an error reposting the request.');
    },
  });

  const { mutate: deleteMutate, isPending: isDeleteMutating } = useMutation({
    mutationFn: (record: any) => RequestFileService.delete(record.telegramMessageId),
    onSuccess: async (data: any) => {
      if (Array.isArray(data?.errors) && data?.errors?.length > 0) {
        data?.errors?.forEach((item: any) => {
          message.error(item);
        });
        return;
      }
      if (data?.errors?.length == 0) {
        message.success('Solicitarea a fost stearsa');
        search();
      }
    },
    onError: (error: any) => {
      console.error('Error processing your request:', error);
      message.error('There was an error deleting the request.');
    },
  });

  // const getTime = useCallback(async (record: any, fromOffice: boolean = true) => {
  //   setIsLoading(true);

  //   // Check if the workPlaceAddress exists
  //   const workPlaceAddress = record?.workPlace?.address;
  //   if (!workPlaceAddress) {
  //     setIsLoading(false);
  //     message.error('Adresa punctului de lucru nu este validă sau lipsește.');
  //     return Promise.reject('Adresa punctului de lucru nu este validă sau lipsește.');
  //   }

  //   const officeAddress = 'Bihor, Loc. Stei, Str. Crinului 2 A, Cod 415600';

  //   return new Promise((resolve) => {
  //     if (navigator.geolocation) {
  //       navigator.geolocation.getCurrentPosition(
  //         async (position) => {
  //           const { latitude, longitude } = position.coords;

  //           const url = `https://api.distancematrix.ai/maps/api/distancematrix/json?origins=${workPlaceAddress}&destinations=${
  //             fromOffice ? officeAddress : `${latitude},${longitude}`
  //           }&key=gKwNeGEwHy60OUVJ6HJzrfO1KMwMFcoQAr2LuBXwUWMGtx7JPBqGmSMzZlDifpfV`;

  //           const headers = {
  //             Accept: 'application/json, charset=utf-8',
  //             'Content-Type': 'application/json',
  //             Authorization: 'gKwNeGEwHy60OUVJ6HJzrfO1KMwMFcoQAr2LuBXwUWMGtx7JPBqGmSMzZlDifpfV',
  //           };

  //           try {
  //             const response = await fetch(url, {
  //               method: 'GET',
  //               headers: headers,
  //             });

  //             if (!response.ok) {
  //               throw message.error(`Eroare HTTP! Status: ${response.status}`);
  //             }

  //             const data = await response.json();

  //             const rows = data?.rows;
  //             if (
  //               rows[0].elements[0].status != 'ZERO_RESULTS' &&
  //               rows &&
  //               rows[0] &&
  //               rows[0].elements &&
  //               rows[0].elements[0]
  //             ) {
  //               const element = rows[0].elements[0];
  //               const durationValue = element?.duration?.value;
  //               const distanceText = element?.distance?.text;

  //               // Handle missing duration or distance
  //               if (!durationValue || !distanceText) {
  //                 throw message.error('Informații despre durata sau distanță lipsesc în răspuns.');
  //               }

  //               const minutes = Math.round(durationValue / 60);

  //               setIsLoading(false);
  //               message.success(`Aproximare timp deplasare spre punct de lucru: ${minutes} minute, ${distanceText}`);
  //               resolve(data);
  //             } else {
  //               // Handle invalid response structure
  //               throw message.error(
  //                 'Structura răspunsului API Distance Matrix nu este validă. Verificați adresa introdusă.'
  //               );
  //             }
  //           } catch (error: any) {
  //             setIsLoading(false);
  //             const errorMessage = error.message || 'Eroare neașteptată în timpul obținerii datelor de rută.';
  //             message.error(`Eroare obținere date: ${errorMessage}`);
  //           }
  //         },
  //         (error) => {
  //           // Handle geolocation errors
  //           setIsLoading(false);
  //           const geolocationError = error.message || 'Imposibil de obținut geolocația.';
  //           message.error(`Eroare cu geolocația: ${geolocationError}`);
  //         }
  //       );
  //     } else {
  //       // Handle browsers that don't support geolocation
  //       setIsLoading(false);
  //       const browserError = 'Geolocația nu este suportată de acest browser.';
  //       message.error(browserError);
  //     }
  //   });
  // }, []);

  const handleVisualization = useCallback((record: any) => {
    window.open(`/service-maintenance/dispatch/view?id=${record.id}`, '_blank');
  }, []);

  const flattenObject = useCallback((obj: any, prefix = ''): Record<string, string> => {
    return Object.keys(obj).reduce((acc, k) => {
      const key = prefix ? `${prefix}.${k}` : k;
      const value = obj[k];

      if (typeof value === 'object' && value !== null) {
        Object.assign(acc, flattenObject(value, key));
      } else {
        acc[key] = value?.toString().toLowerCase();
      }
      return acc;
    }, {} as Record<string, string>);
  }, []);

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxRequestFile.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: any[] = useMemo(
    () => [
      {
        title: 'Numar',
        dataIndex: 'number',
        key: 'number',
        render: (number: number) => number + '. ',
      },
      {
        title: 'Client',
        key: 'company',
        render: (record: any) => {
          const company = record?.metadata?.company ?? record?.company;
          return <div>{company?.name}</div>;
        },
      },
      {
        title: 'Punct',
        dataIndex: '',
        render: (record: any) => {
          return <strong>{record?.metadata?.workPlace?.name ?? record?.workPlace?.name}</strong>;
        },
        key: 'workPlaceName',
      },
      {
        title: 'Echipament',
        dataIndex: 'requestFileSystemTypes',
        key: 'requestFileSystemTypes',
        render: (requestFileSystemTypes: any[]) => (
          <strong>
            {requestFileSystemTypes && requestFileSystemTypes?.length > 0 && (
              <span>
                {requestFileSystemTypes?.length > 0
                  ? requestFileSystemTypes?.map((type: any) => `[${type?.systemType?.name}]`).join(' ')
                  : ''}
              </span>
            )}
          </strong>
        ),
      },
      {
        title: 'Descriere',
        dataIndex: '',
        key: 'description',
        render: (record: any) => (
          <div style={{ maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <b>Locatie: </b>
            <span title={record?.workPlace?.address}>{record?.workPlace?.address}</span>
            <br />
            <b>Informatii locatie: </b>
            <span title={record?.workPlace?.locationInfo}>{record?.workPlace?.locationInfo}</span>
            <br />
            <b>Descriere problema: </b>
            <span title={record?.remarks}>{record?.remarks}</span>
          </div>
        ),
      },
      {
        title: 'Tehnician',
        dataIndex: 'assignedTechnician',
        key: 'assignedTechnicianName',
        render: (assignedTechnician: any) => (
          <>
            <div>{assignedTechnician?.name ?? ' - '}</div>
          </>
        ),
      },
      {
        title: 'Urgenta',
        dataIndex: 'isUrgent',
        key: 'isUrgent',
        render: (isUrgent: any) => (
          <>
            <Tag color={isUrgent ? 'red' : 'blue'}> {isUrgent ? 'DA' : 'NU'}</Tag>
          </>
        ),
      },
      {
        title: 'Data programata',
        dataIndex: 'interventionDate',
        key: 'interventionDate',
        render: (date: string) => <>{formatDate(date)}</>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: any) => (
          <>
            <div>{status?.name}</div>
          </>
        ),
      },
      {
        title: 'Actiuni',
        dataIndex: '',
        key: 'actions',
        render: (record: any) => (
          <Space size='middle'>
            <Tooltip title='Vizualizeaza'>
              <Button icon={<EyeOutlined />} onClick={() => handleVisualization(record)}></Button>
            </Tooltip>
            {record.statusId == 1 && profile?.userPermission?.rols.includes('senior_tehnician') && (
              <Tooltip title='Reposteaza'>
                <Button icon={<NotificationOutlined />} onClick={() => repostMutate(record)}></Button>{' '}
              </Tooltip>
            )}
            {/* <Tooltip title={`Timp parcurs sediu -> ${record.workPlace?.name}`}>
              <Button icon={<FieldTimeOutlined />} onClick={() => getTime(record)}></Button>
            </Tooltip>
            <Tooltip title={`Timp parcurs tehnician -> ${record.workPlace?.name}`}>
              <Button icon={<ClockCircleOutlined />} onClick={() => getTime(record, false)}></Button>
            </Tooltip> */}
            {record.statusId == 1 &&
              (profile?.userPermission?.rols.includes('senior_tehnician') ||
                profile?.userPermission?.rols.includes('dispatcher') ||
                profile?.userPermission?.rols.includes('admin')) && (
                <Tooltip title='Sterge'>
                  <Button icon={<DeleteOutlined />} danger onClick={() => deleteMutate(record)}></Button>{' '}
                </Tooltip>
              )}
          </Space>
        ),

        align: 'center',
      },
    ],
    [data, searchTerm, searchStatus]
  );

  return (
    <>
      <h1>Solicitari</h1>
      <div style={{ display: 'grid' }}>
        <Space style={{ marginBottom: 16, justifyContent: 'flex-end' }}>
          <Tooltip title='Creaza solicitare corectiva' trigger={'hover'} placement='left'>
            <Button
              type='primary'
              onClick={() => window.open('/service-maintenance/dispatch/new', '_blank')}
              icon={<PlusOutlined />}
              disabled={!usePermission(['dispatcher'])}
              shape='circle'
            />
          </Tooltip>
        </Space>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search placeholder='Cauta' onChange={handleSearchChange} style={{ width: '100%' }} allowClear />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker
              placeholder='Filtreaza dupa data'
              value={selectedDate}
              onChange={handleDateChange}
              style={{ width: '100%' }}
              format='DD-MM-YYYY'
              allowClear
            />
          </Col>{' '}
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder='Filtreaza dupa status'
              onChange={handleStatusChange}
              style={{ width: '100%' }}
              allowClear
            >
              <Select.Option value='1'>Nepreluata</Select.Option>
              <Select.Option value='2'>Preluata</Select.Option>
              <Select.Option value='3'>Am ajuns</Select.Option>
              <Select.Option value='4'>In lucru</Select.Option>
              <Select.Option value='5'>Finalizata</Select.Option>
            </Select>
          </Col>
        </Row>
      </div>
      {isRequestFileLoading || isRepostMutating || isDeleteMutating ? (
        <Spin size='large' className='spinner' />
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey='id'
          pagination={paginationConfig}
          scroll={{ x: 'max-content' }}
        />
      )}
    </>
  );
};

export default Dispatch;
